import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Fragment, useRef, useState, useEffect } from "react";
import { FcCalculator } from "react-icons/fc";

export interface EmiCalculatorProps {
  isOpen: boolean;
  setOpen: (open: boolean) => void;
}

interface EmiResult {
  monthlyEmi: number;
  totalInterest: number;
  totalAmount: number;
}

export default function EmiCalculator({ isOpen, setOpen }: EmiCalculatorProps) {
  const [principal, setPrincipal] = useState<number>(null);
  const [interestRate, setInterestRate] = useState<number>(8.5);
  const [tenure, setTenure] = useState<number>(3);
  const [emiResult, setEmiResult] = useState<EmiResult>({
    monthlyEmi: 0,
    totalInterest: 0,
    totalAmount: 0,
  });

  const handleClose = () => {
    setOpen(false);
  };

  const cancelButtonRef = useRef(null);

  const calculateEMI = (p: number, r: number, n: number): EmiResult => {
    const monthlyRate = r / 12 / 100;
    const numberOfPayments = n * 12;

    if (monthlyRate === 0) {
      const monthlyEmi = p / numberOfPayments;
      return {
        monthlyEmi,
        totalInterest: 0,
        totalAmount: p,
      };
    }

    const monthlyEmi =
      (p * monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) /
      (Math.pow(1 + monthlyRate, numberOfPayments) - 1);

    const totalAmount = monthlyEmi * numberOfPayments;
    const totalInterest = totalAmount - p;

    return {
      monthlyEmi,
      totalInterest,
      totalAmount,
    };
  };

  useEffect(() => {
    const result = calculateEMI(principal, interestRate, tenure);
    setEmiResult(result);
  }, [principal, interestRate, tenure]);

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-40" initialFocus={cancelButtonRef} onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="sticky top-0" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full  justify-end p-4 pt-40">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-3xl h-96 transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all">
                <div className="bg-gray-800 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10">
                        {/* <Calculator className="h-5 w-5 text-white" /> */}
                        <FcCalculator className="w-6 h-6" />
                      </div>
                      <div>
                        <Dialog.Title className="text-xl font-semibold text-white">EMI Calculator</Dialog.Title>
                        <p className="text-blue-100 text-xs">Calculate your EMI repayment details</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="rounded-full bg-white/10 p-2 text-white hover:bg-white/20 transition-colors"
                      onClick={handleClose}
                      ref={cancelButtonRef}
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <div className="px-6  p-3">
                  <div className="">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center pb-1">
                      {/* <BiDollar className="h-5 w-5 mr-2 text-blue-600" /> */}
                      Loan Details
                    </h3>
                    {/* Input Section */}
                    <div className="grid grid-cols-2 gap-6 ">
                      {/* Principal Amount */}
                      <div className="space-y-3">
                        <label className="block text-sm font-medium text-gray-700">Principal Amount</label>
                        <div className="space-y-2">
                          <input
                            type="number"
                            min={0}
                            autoFocus={true}
                            value={principal}
                            maxLength={5}
                            // onChange={(e) => setPrincipal(e.target.value as unknown as number)}
                            onChange={(e) => {
                              const value = e.target.value;
                              const numericValue = Number(value);

                              if (value === "") {
                                setPrincipal(0); // or optionally allow blank
                              } else if (!isNaN(numericValue)) {
                                setPrincipal(numericValue);
                              }
                            }}
                            className="w-full px-4 py-3 text-lg font-semibold border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter amount"
                          />
                        </div>
                      </div>

                      <div className="space-y-3">
                        <label className="block text-sm font-medium text-gray-700">Loan Tenure</label>
                        <div className="space-y-2">
                          <input
                            type="number"
                            value={tenure}
                            disabled
                            onChange={(e) => setTenure(Number(e.target.value))}
                            className="w-full px-4 py-3 text-lg font-semibold border bg-gray-100 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Tenure in years"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-1 font-medium text-sm text-gray-900 py-4">
                      <span className="text-sm">Down Payment :</span>
                      <span>{formatCurrency(principal / 4)}</span>
                    </div>

                    {/* Results Section */}
                    {/* EMI Breakdown Table */}
                    <div className="">
                      <div className=" rounded-md border border-gray-200 overflow-x-auto">
                        <table className="divide-y w-full divide-gray-200 text-sm text-center">
                          <thead className="bg-blue-100 text-blue-700 font-medium">
                            <tr>
                              <th className="p-3 text-left">Month 1</th>
                              <th className="p-3 text-left">Month 2</th>
                              <th className="p-3 text-left">Month 3</th>
                              <th className="p-3 text-left">Total EMI</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white text-gray-800">
                            <tr>
                              {[1, 2, 3].map((month) => {
                                const downPayment = principal / 4;
                                const remainingPrincipal = principal - downPayment;
                                const monthlyEmi = remainingPrincipal / 3;
                                return (
                                  <td key={month} className="text-left p-3">
                                    {formatCurrency(monthlyEmi)}
                                  </td>
                                );
                              })}
                              <td className=" text-left p-3 font-semibold text-green-700">
                                {formatCurrency((principal * 3) / 4)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
